"use client"

import { useState } from "react"
import { Input } from "../../Catalyst/input"
import SendInviteIcon from "../../../assets/SendInviteIcon"
import { Switch } from "../../Catalyst/switch"
import { createUser } from "../../../services/UsersService"
import { useOrgId } from "../../../hooks/useOrgId"
import { useToast } from "../../../contexts/ToastContext"

export function SendManuallyComponent() {
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const orgId = useOrgId()
  const { showSuccessToast, showErrorToast } = useToast()

  const isFormValid = name.trim().length > 0 && validateEmail(email) && orgId !== null

  function validateEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleInvite = async () => {
    if (!isFormValid || !orgId) return;
    
    setIsLoading(true)
    
    try {
      // Parse name into first and last
      const nameParts = name.trim().split(' ')
      const first = nameParts[0] || ''
      const last = nameParts.slice(1).join(' ') || ''
      
      const userData = {
        organization_id: orgId,
        email: email.trim(),
        first,
        last,
        user_role: "FINLEY_USER",
        send_invite: sendWelcomeEmail
      }
      
      await createUser(userData)
      
      const successMessage = sendWelcomeEmail 
        ? `We've sent a welcome email to ${first} ${last} with everything they need to get started. You can check their activation status in your dashboard.`
        : `${first} ${last} has been added to your team's benefits platform. You can send them a welcome invite anytime from your dashboard.`
      
      showSuccessToast('New user successfully added', successMessage)
      
      // Reset form
      setName("")
      setEmail("")
      setSendWelcomeEmail(false)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.msg || err?.response?.data?.message || "Failed to create user. Please try again."
      showErrorToast('Failed to create user', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h3 className="font-['Inter'] text-[14px] font-medium leading-[20px] text-[#525252] mb-1">Send Invites Manually</h3>
      <p className="font-['Inter'] text-[12px] font-medium leading-[20px] text-[#525252] mb-3">
        Sending an invite manually is a great way to add one more users. They will receive an email to access their
        Finley account.
      </p>

      <div className="mb-3 flex gap-3">
        <div className="flex-1">
          <label htmlFor="name" className="block font-['Inter'] font-medium text-[14px] leading-[20px] text-slate-900 mb-1.5">
            User's First and Last Name
          </label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First Last"
            className="border-gray-300 text-gray-900 placeholder:text-gray-400"
            disabled={isLoading}
          />
        </div>
        <div className="flex-1">
          <label htmlFor="email" className="block font-['Inter'] font-medium text-[14px] leading-[20px] text-slate-900 mb-1.5">
            User's Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="border-gray-300 text-gray-900 placeholder:text-gray-400"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <div className={`flex items-start gap-2 ${isLoading ? 'opacity-50' : ''}`}>
          <Switch
            color="green"
           
            checked={sendWelcomeEmail}
            onChange={setSendWelcomeEmail}
          />
          <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#344054]">Send welcome email</span>
        </div>
        <div className={`${!isFormValid || isLoading ? 'opacity-50' : ''}`}>
          <button
            onClick={handleInvite}
            disabled={!isFormValid || isLoading}
            className="bg-white border border-neutral-200 rounded-lg shadow-sm px-3.5 py-2.5 flex items-center gap-1 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
          >
            <SendInviteIcon />
            <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-neutral-900">
              {isLoading ? 'Sending...' : 'Invite User'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
